import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import { ShareButton } from "@/components/ShareButton";
import { downloadVerseCard } from "@/lib/verseCard";
import { composePoemImage } from "@/lib/composePoemImage";
import { toast } from "sonner";
import { Droplet, Upload, Trash2, Loader2, ImageDown, Plus, Check, Clock } from "lucide-react";

function SubmitForm({ isAdmin, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [poem, setPoem] = useState("");
  const [author, setAuthor] = useState(isAdmin ? "CS" : "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose an image first.");
    setBusy(true);
    try {
      let toUpload = file;
      // Poem over photo: bake the words onto the image automatically.
      if (poem.trim()) {
        try {
          const blob = await composePoemImage(file, { poem: poem.trim(), author: author.trim() });
          toUpload = new File([blob], "well-piece.jpg", { type: "image/jpeg" });
        } catch {
          toUpload = file; // fall back to the raw photo if compositing fails
        }
      }
      const fd = new FormData();
      fd.append("image", toUpload);
      fd.append("poem", poem);
      fd.append("author", author || "");
      fd.append("note", note);
      const r = await api.post("/soul-verses", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.pending ? "Thank you — submitted for approval." : "Added to The Well.");
      setFile(null); setPreview(""); setPoem(""); setNote(""); setOpen(false);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        data-testid="well-add-toggle"
        onClick={() => setOpen(true)}
        className="mb-12 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-6 py-3 font-medium hover:bg-[#e6c860] transition-colors duration-300 gold-glow"
      >
        <Plus size={18} /> {isAdmin ? "Add to the Well" : "Share your own"}
      </button>
    );
  }

  return (
    <form data-testid="well-uploader" onSubmit={submit} className="mb-12 glass rounded-2xl p-6 border border-[#D4AF37]/25 space-y-4">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
        <Upload size={14} /> {isAdmin ? "Add a new piece" : "Offer a piece to the Well"}
      </p>
      {!isAdmin && (
        <p className="text-sm text-[#B4A897]">Your piece will be gently reviewed before it appears — thank you for sharing.</p>
      )}
      <label className="block cursor-pointer">
        <span className="text-xs uppercase tracking-[0.2em] text-[#B4A897]">Image</span>
        <input data-testid="well-file-input" type="file" accept="image/*" onChange={(e) => pick(e.target.files[0])} className="mt-2 block w-full text-sm text-[#B4A897] file:mr-4 file:rounded-full file:border-0 file:bg-[#D4AF37] file:text-[#17100C] file:px-4 file:py-2 file:font-medium" />
      </label>
      {preview && <img src={preview} alt="preview" className="rounded-xl max-h-64 w-auto" />}
      <div>
        <span className="text-xs uppercase tracking-[0.2em] text-[#B4A897]">Poem (optional — laid onto your photo automatically)</span>
        <textarea data-testid="well-poem-input" value={poem} onChange={(e) => setPoem(e.target.value)} rows={4}
          placeholder="Your words…" className="mt-2 w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-[#F7F1E6] outline-none focus:border-[#D4AF37]/60 transition-colors duration-300 resize-none" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-[#B4A897]">Author</span>
          <input data-testid="well-author-input" value={author} onChange={(e) => setAuthor(e.target.value)}
            placeholder="Your name or initials" className="mt-2 w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-[#F7F1E6] outline-none focus:border-[#D4AF37]/60 transition-colors duration-300" />
        </div>
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-[#B4A897]">Note (optional)</span>
          <input data-testid="well-note-input" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="A short reflection" className="mt-2 w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-[#F7F1E6] outline-none focus:border-[#D4AF37]/60 transition-colors duration-300" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button data-testid="well-submit" type="submit" disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-6 py-2.5 font-medium hover:bg-[#e6c860] transition-colors duration-300 disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {busy ? "Sending…" : (isAdmin ? "Add to the Well" : "Submit for approval")}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-white/10 px-6 py-2.5 text-sm text-[#B4A897] hover:text-[#F7F1E6] transition-colors duration-300">
          Cancel
        </button>
      </div>
    </form>
  );
}

function PendingQueue({ pending, onApprove, onReject }) {
  if (!pending.length) return null;
  return (
    <div data-testid="well-pending" className="mb-14">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-5">
        <Clock size={14} /> Awaiting your blessing ({pending.length})
      </p>
      <div className="grid sm:grid-cols-2 gap-6">
        {pending.map((v, i) => (
          <div key={v.id} data-testid={`well-pending-${i}`} className="glass rounded-2xl overflow-hidden border border-[#D4AF37]/20">
            <img src={v.image_url} alt={v.poem || "pending"} className="w-full h-auto block" />
            <div className="p-4">
              {v.poem && <p className="italic-serif text-[#F7F1E6] whitespace-pre-line">{v.poem}</p>}
              <p className="mt-2 text-sm text-[#D4AF37]">— {v.author}</p>
              {v.note && <p className="mt-1 text-sm text-[#B4A897]">{v.note}</p>}
              <div className="mt-4 flex gap-3">
                <button data-testid={`well-approve-${i}`} onClick={() => onApprove(v.id)}
                  className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-5 py-2 text-sm font-medium hover:bg-[#e6c860] transition-colors duration-300">
                  <Check size={15} /> Bless & publish
                </button>
                <button data-testid={`well-reject-${i}`} onClick={() => onReject(v.id)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 text-[#B4A897] px-5 py-2 text-sm hover:text-[#C1272D] transition-colors duration-300">
                  <Trash2 size={15} /> Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TheWell() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isPaid = user && (user.tier === "seeker" || user.tier === "contributor");
  const canContribute = isAdmin || user?.tier === "contributor";
  const [verses, setVerses] = useState([]);
  const [pending, setPending] = useState([]);

  const load = useCallback(() => {
    api.get("/soul-verses").then((r) => setVerses(r.data.verses || [])).catch(() => {});
    if (isAdmin) api.get("/soul-verses/pending").then((r) => setPending(r.data.verses || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await api.post(`/soul-verses/${id}/approve`); toast.success("Published to The Well."); load(); }
    catch { toast.error("Could not publish."); }
  };
  const remove = async (id) => {
    try { await api.delete(`/soul-verses/${id}`); toast.success("Removed."); load(); }
    catch { toast.error("Could not remove."); }
  };

  return (
    <main data-testid="the-well-page" className="max-w-5xl mx-auto px-6 py-16">
      <div className="max-w-3xl mb-10 fade-up">
        <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4 flex items-center gap-2">
          <Droplet size={14} /> The Well
        </p>
        <h1 className="serif text-4xl sm:text-6xl font-light text-[#F7F1E6]">
          Living words, <span className="italic-serif text-[#D4AF37]">drawn deep.</span>
        </h1>
        <p className="mt-5 text-[#B4A897] leading-relaxed text-lg">
          Original poems and images offered from the heart — a well to return to, kept filling
          by many hands and one Light.
        </p>
      </div>

      {isAdmin && <PendingQueue pending={pending} onApprove={approve} onReject={remove} />}
      {canContribute && <SubmitForm isAdmin={isAdmin} onDone={load} />}
      {user && !canContribute && (
        <div data-testid="well-contribute-cta" className="mb-12 glass rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4 border border-[#D4AF37]/20">
          <p className="text-[#B4A897]">Want to add your own poem or photo to the Well, and help others on their way?</p>
          <a href="/pricing" className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-6 py-2.5 font-medium hover:bg-[#e6c860] transition-colors duration-300">
            Become a Contributor
          </a>
        </div>
      )}
      {!user && (
        <p className="mb-12 text-[#B4A897] italic-serif text-lg">
          Sign in to offer a poem or image of your own to the Well.
        </p>
      )}

      <div className="space-y-16" data-testid="soul-verses">
        {verses.map((v, i) => (
          <figure key={v.id} data-testid={`soul-verse-${i}`} className="fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
            <div className="glass rounded-3xl overflow-hidden relative">
              <img src={v.image_url} alt={v.poem || v.note || "A piece from The Well"} loading="lazy" className="w-full h-auto block" />
              {isAdmin && (
                <button
                  data-testid={`well-delete-${i}`}
                  onClick={() => remove(v.id)}
                  className="absolute top-4 right-4 rounded-full bg-black/50 backdrop-blur text-white/80 hover:text-[#C1272D] p-2 transition-colors duration-300"
                  title="Remove"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
            <figcaption className="mt-6">
              {v.poem && (
                <blockquote className="italic-serif text-2xl sm:text-3xl text-[#F7F1E6] leading-snug whitespace-pre-line">
                  {v.poem}
                </blockquote>
              )}
              {v.author && <p className="mt-3 text-[#D4AF37] tracking-wide">— {v.author}</p>}
              {v.note && <p className="mt-4 text-[#B4A897] leading-relaxed max-w-2xl">{v.note}</p>}
              <div className="mt-5 flex flex-wrap gap-3">
                <ShareButton
                  testId={`share-soul-verse-${i}`}
                  title="A word from The Well — Mirrors of Your Soul"
                  text={`${v.poem || v.note}\n\n— ${v.author}\n\nFrom The Well · Mirrors of Your Soul`}
                  label="Share this"
                />
                {v.poem && isPaid && (
                  <button
                    data-testid={`card-soul-verse-${i}`}
                    onClick={() => downloadVerseCard({ text: v.poem, attribution: v.author, filename: `the-well-${v.id}` })}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 text-[#A8A29E] px-5 py-2 text-sm hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-300"
                  >
                    <ImageDown size={15} /> Save as card
                  </button>
                )}
              </div>
            </figcaption>
          </figure>
        ))}
        {verses.length === 0 && (
          <p className="text-[#B4A897] italic-serif text-lg">The well is being filled. Return soon.</p>
        )}
      </div>
    </main>
  );
}
